import type { AllReporterVariant } from '../../hooks/useReporter';

import { Editor, Node } from '../../core';
import { HeadingLevel } from '../heading/constants';
import { HEADING_PLUGIN_ID } from '../heading/types';
import { Element } from 'slate';

export const getDuplicateHeadingErrorText = (variant: AllReporterVariant): string => {
  switch (variant) {
    case 'report':
      return 'Cannot sign report with duplicate section headers';
    case 'template':
      return 'Cannot save template with duplicate section headers';
    default:
      return 'Cannot have duplicate section headers';
  }
};

export const normalizeText = (text: string): string => {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^\w\s']|_/g, '')
    .replace(/\s+/g, ' ');
};

export const isH1HeadingNode = (node: Node): boolean => {
  return (
    Element.isElement(node) && node.type === HEADING_PLUGIN_ID && node.level === HeadingLevel.H1
  );
};

export const doesH1HeadingMatchText = (node: Node, normalizedHeadingText: string): boolean => {
  return isH1HeadingNode(node) && normalizeText(Node.string(node)) === normalizedHeadingText;
};

export const hasDuplicateHeadingsInNode = (root: Node): boolean => {
  const seenHeadings = new Set<string>();

  for (const [node] of Array.from(Node.nodes(root))) {
    if (!isH1HeadingNode(node)) {
      continue;
    }

    const headingText = normalizeText(Node.string(node));
    if (headingText === '') {
      continue;
    }

    if (seenHeadings.has(headingText)) {
      return true;
    }

    seenHeadings.add(headingText);
  }

  return false;
};

export const reportHasDuplicateHeadings = (editor: Editor, text?: string): boolean => {
  const normalizedText = text == null ? undefined : normalizeText(text);

  return (
    Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (n) =>
          isH1HeadingNode(n) &&
          (normalizedText == null || doesH1HeadingMatchText(n, normalizedText)) &&
          Array.from(Node.texts(n)).some(
            (textNodeEntry) => (textNodeEntry[0] as any).headingError === true
          ),
      })
    ).length > 0
  );
};
